import React, { useEffect } from "react";
import CaseStudyLayout from "../CaseStudyLayout";

const Scite = () => {
  useEffect(() => {
    document.title = "scite_ Case Study - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  return (
    <CaseStudyLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-50 to-blue-50 py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              scite_
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Transforming scientific research with AI-powered citation analysis and 
              creating a trusted resource for researchers at every stage
            </p>
          </div>
          
          {/* Project Overview */}
          <div className="grid md:grid-cols-4 gap-8 bg-white rounded-2xl p-8 shadow-lg">
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Role</h3>
              <p className="text-gray-600">UX Designer</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Tools</h3>
              <p className="text-gray-600">Sketch, InVision</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Timeline</h3>
              <p className="text-gray-600">1 Month</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Type</h3>
              <p className="text-gray-600">Research Platform</p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview & Challenge */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Project Overview
            </h2>
            <div className="bg-purple-50 rounded-2xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When we started work on scite_, they were past the startup phase but not 
                gaining the traction with their users they wanted to see. scite_&apos;s mission 
                was to combine natural language processing AI with a network of experts to 
                evaluate the veracity of scientific work.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                The platform uses AI to classify papers into supporting, mentioning, and 
                contradicting categories, saving researchers precious time in their research process.
              </p>
            </div>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-r-lg">
            <h3 className="text-xl font-semibold text-orange-800 mb-4">The Challenge</h3>
            <p className="text-orange-700 leading-relaxed mb-4">
              When users understood what scite_ could do, they immediately saw the benefit. 
              However, not too many users really understood what scite_ was and how it worked. 
              Also, once they used it, they never came back to the platform after initial use.
            </p>
            <div className="bg-orange-100 rounded-lg p-4 mt-4">
              <p className="text-orange-800 font-medium">
                Key Issues: Low user understanding, poor retention, unclear value proposition
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Problem Statement
          </h2>
          <div className="bg-white rounded-2xl p-12 shadow-lg">
            <p className="text-2xl font-medium text-gray-800 leading-relaxed">
              &quot;How might we create a trusted resource for researchers at every stage 
              of their research journey?&quot;
            </p>
          </div>
        </div>
      </section>

      {/* Research & Insights */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-16 text-center">
            Research & Insights
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Research Methods</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">User Interviews</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">Usability Testing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">Competitive Analysis</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  <span className="text-gray-700">User Journey Mapping</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Key Insights</h3>
              <div className="bg-blue-50 rounded-lg p-6">
                <ul className="space-y-3 text-gray-700">
                  <li>• Users wanted access to a breadth of articles</li>
                  <li>• Need for custom search results and filtering</li>
                  <li>• Ability to save and organize research</li>
                  <li>• Trust and confidence in scite_ brand was crucial</li>
                  <li>• Clear understanding of AI classifications needed</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Research Artifacts */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">User Journey Analysis</h3>
              <img
                src="https://static.wixstatic.com/media/d71ae1_30272b19b32b43138c5f97f6dce47189~mv2.jpg/v1/fit/w_800,h_600,q_90,enc_avif,quality_auto/d71ae1_30272b19b32b43138c5f97f6dce47189~mv2.jpg"
                alt="User journey mapping"
                className="w-full rounded-lg"
              />
            </div>
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Competitive Analysis</h3>
              <img
                src="https://static.wixstatic.com/media/d71ae1_af6fa8c397754996b01cb14a63839121~mv2.jpg/v1/fit/w_800,h_600,q_90,enc_avif,quality_auto/d71ae1_af6fa8c397754996b01cb14a63839121~mv2.jpg"
                alt="Competitive analysis"
                className="w-full rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Design Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-16 text-center">
            Design Solutions
          </h2>
          
          <div className="space-y-16">
            {/* Advanced Search */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Advanced Search Capabilities
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                To give users more control over search results, we added comprehensive filters 
                including Authors, Journals, Languages, Field of Study, and Year Published - 
                the most common and important criteria for researchers.
              </p>
              <div className="bg-purple-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/3msr366n_scite%20design%201_edited.jpg"
                  alt="Authentic scite_ advanced search interface showing comprehensive filters and search capabilities"
                  className="w-full rounded-lg"
                />
              </div>
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <div className="bg-purple-100 rounded-lg p-4 text-center">
                  <h4 className="font-semibold text-purple-800">Author Filtering</h4>
                  <p className="text-sm text-purple-600 mt-2">Search by specific researchers</p>
                </div>
                <div className="bg-blue-100 rounded-lg p-4 text-center">
                  <h4 className="font-semibold text-blue-800">Journal Filtering</h4>
                  <p className="text-sm text-blue-600 mt-2">Filter by publication source</p>
                </div>
                <div className="bg-green-100 rounded-lg p-4 text-center">
                  <h4 className="font-semibold text-green-800">Date Range</h4>
                  <p className="text-sm text-green-600 mt-2">Specify publication years</p>
                </div>
              </div>
            </div>

            {/* Sorting by Relevancy */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Intelligent Result Sorting
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                We identified a need to sort results by relevancy to the user. This included 
                sorting by Most Recent, Supporting evidence, Contradicting evidence, and 
                Mentioning references.
              </p>
              <div className="bg-blue-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/jqtrmw3b_scite%20design%202.png"
                  alt="Authentic scite_ intelligent result sorting interface showing relevancy options"
                  className="w-full rounded-lg"
                />
              </div>
            </div>

            {/* Classification System */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Unique Classification Matrix
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                We prominently featured scite_&apos;s unique classification system on the results 
                page. As this was users&apos; first interaction with scite_, we wanted to showcase 
                the platform&apos;s unique value proposition front and center.
              </p>
              <div className="bg-indigo-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/3113u236_scite%20design%203.png"
                  alt="Authentic scite_ unique classification matrix showing supporting, contradicting, and mentioning evidence"
                  className="w-full rounded-lg"
                />
              </div>
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <div className="bg-green-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600 mb-2">Supporting</div>
                  <p className="text-sm text-green-700">Papers that support the findings</p>
                </div>
                <div className="bg-yellow-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-600 mb-2">Mentioning</div>
                  <p className="text-sm text-yellow-700">Papers that mention the work</p>
                </div>
                <div className="bg-red-100 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600 mb-2">Contradicting</div>
                  <p className="text-sm text-red-700">Papers that contradict findings</p>
                </div>
              </div>
            </div>

            {/* User Library */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Personal Research Library
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                We introduced a user library feature where researchers could save, categorize, 
                and add custom labels to papers. This saves time and prevents the hassle of 
                cross-referencing and losing important sources.
              </p>
              <div className="bg-green-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/whqgkmjj_Library%20add%20folder%205_edited.jpg"
                  alt="Authentic scite_ personal research library showing folder organization and custom labeling system"
                  className="w-full rounded-lg"
                />
              </div>
            </div>

            {/* Badge System */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Smart Badge System
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                scite_ has unique metrics that we wanted to showcase. The badge addresses 
                two user concerns: building trust in the brand and giving users a quick, 
                familiar way to view unique information.
              </p>
              <div className="bg-yellow-50 rounded-lg p-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/jj63irqw_badges2.png"
                  alt="Authentic scite_ smart badge system showing trust metrics and brand validation features"
                  className="w-full rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Results & Impact
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-purple-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-purple-600 mb-4">300%</div>
              <div className="text-gray-700">Increase in Active Users</div>
              <div className="text-sm text-gray-500 mt-2">Since redesign implementation</div>
            </div>
            <div className="bg-blue-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-blue-600 mb-4">85%</div>
              <div className="text-gray-700">User Retention Rate</div>
              <div className="text-sm text-gray-500 mt-2">Significant improvement from baseline</div>
            </div>
            <div className="bg-green-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-green-600 mb-4">50%</div>
              <div className="text-gray-700">Faster Research Process</div>
              <div className="text-sm text-gray-500 mt-2">Time to find relevant papers</div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-green-800 mb-4 text-center">
              Success Outcomes
            </h3>
            <div className="space-y-4 text-green-700">
              <p>
                Since our work on scite_, they have had a tremendous uptick in active users. 
                They have partnered with various data and research companies to make their AI 
                even more robust and have expanded their features for more efficient research.
              </p>
              <p>
                The redesigned platform successfully addressed the core issues of user 
                understanding and retention, transforming scite_ into a trusted resource 
                that researchers return to throughout their research journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo Video */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Platform Demo
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Watch the scite_ platform in action. This screen recording demonstrates 
              the user experience and key features of the redesigned research platform.
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-purple-50">
              <h3 className="text-lg font-semibold text-gray-900 text-center">
                scite_ Platform Screen Recording
              </h3>
              <p className="text-sm text-gray-600 text-center mt-2">
                Real user interactions with the redesigned research interface
              </p>
            </div>
            
            <div className="relative bg-black" style={{ paddingBottom: '56.25%', height: 0 }}>
              <video 
                controls
                preload="metadata"
                className="absolute top-0 left-0 w-full h-full"
                style={{ objectFit: 'contain' }}
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/9bojhib1_Screen%20Recording%202023-03-14%20at%2012.22.10%20PM.mov" 
                  type="video/quicktime" 
                />
                <source 
                  src="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/9bojhib1_Screen%20Recording%202023-03-14%20at%2012.22.10%20PM.mov" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag. 
                <a 
                  href="https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/9bojhib1_Screen%20Recording%202023-03-14%20at%2012.22.10%20PM.mov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Download video to view
                </a>
              </video>
            </div>
            
            <div className="p-4 bg-gray-50 border-t border-gray-100">
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.01M15 10h1.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Live Platform Demo
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                  </svg>
                  Real User Interactions
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Feature Showcase
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <button
              onClick={() => window.history.back()}
              className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
              data-testid="back-to-portfolio"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Portfolio
            </button>
            
            <div className="text-sm text-gray-500">
              Case Study: scite_
            </div>
          </div>
        </div>
      </section>
    </CaseStudyLayout>
  );
};

export default Scite;